import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Rider.css';

const ACTIVE_STATUSES = ['accepted', 'assigned', 'confirmed', 'processing', 'out-for-delivery'];

export default function RiderOrders() {
  const { api, user } = useContext(AuthContext);

  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [refreshing, setRefreshing]       = useState(false);
  const [filter, setFilter]               = useState('all');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const riderId   = String(user?.id || '');
  const riderRole = user?.role?.toLowerCase();

  // ✅ Socket ab App.jsx mein hai — yahan se hata diya
  // Notification bhi App.jsx se handle hoti hai

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      setError('');
      const response = await api.get('/orders');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.orders || response.data?.data || []);

      const myOrders = data.filter((order) => {
        const orderRiderId = String(order?.rider_id || order?.riderId || order?.assigned_rider || '');
        const status       = order?.status?.toLowerCase();
        const isMine              = orderRiderId && orderRiderId === riderId;
        const isUnassignedPending = status === 'pending' && !orderRiderId;
        return isMine || isUnassignedPending;
      });

      setOrders(myOrders);
    } catch (err) {
      console.error('Fetch orders error:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => { setRefreshing(true); fetchOrders(); };

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const ad = new Date(a?.created_at || a?.createdAt || 0).getTime();
      const bd = new Date(b?.created_at || b?.createdAt || 0).getTime();
      return bd - ad;
    });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (filter === 'active') {
      return sortedOrders.filter((o) => {
        const status   = o?.status?.toLowerCase();
        const oRiderId = String(o?.rider_id || o?.riderId || '');
        return oRiderId === riderId && ACTIVE_STATUSES.includes(status);
      });
    }
    if (filter === 'delivered') {
      return sortedOrders.filter((o) => {
        const oRiderId = String(o?.rider_id || o?.riderId || '');
        return oRiderId === riderId && o?.status?.toLowerCase() === 'delivered';
      });
    }
    if (filter === 'cancelled') {
      return sortedOrders.filter((o) => {
        const oRiderId = String(o?.rider_id || o?.riderId || '');
        return oRiderId === riderId && o?.status?.toLowerCase() === 'cancelled';
      });
    }
    return sortedOrders;
  }, [sortedOrders, filter, riderId]);

  const stats = useMemo(() => {
    const mine      = sortedOrders.filter((o) => String(o?.rider_id || '') === riderId);
    const delivered = mine.filter((o) => o?.status?.toLowerCase() === 'delivered');
    const cancelled = mine.filter((o) => o?.status?.toLowerCase() === 'cancelled');
    const active    = mine.filter((o) => ACTIVE_STATUSES.includes(o?.status?.toLowerCase()));
    const now       = new Date();
    const monthDelivered = delivered.filter((o) => {
      const d = new Date(o?.created_at || o?.createdAt || 0);
      if (isNaN(d.getTime())) return false;
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    return {
      total:         sortedOrders.length,
      active:        active.length,
      delivered:     delivered.length,
      cancelled:     cancelled.length,
      monthEarnings: monthDelivered.length * 120
    };
  }, [sortedOrders, riderId]);

  const handleStatusUpdate = async (order, newStatus) => {
    const orderId = order?.id;
    try {
      if (!orderId) return alert('Order ID missing');
      if (!riderId) return alert('Rider ID missing. Please login again.');

      setUpdatingOrderId(orderId);

      const payload = { status: newStatus };
      if (riderRole === 'rider') payload.rider_id = Number(riderId) || riderId;
      if (newStatus === 'delivered') payload.delivered_at = new Date().toISOString();

      const response = await api.patch(`/orders/${orderId}`, payload);
      const updated  = response?.data?.order;

      if (updated) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...updated } : o));
      } else {
        await fetchOrders();
      }
      alert('Status updated successfully!');
    } catch (err) {
      console.error('Update status error:', err);
      alert(err?.response?.data?.message || 'Failed to update status');
      await fetchOrders();
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const showStatusOptions = (order) => {
    const current = order?.status?.toLowerCase();
    const options = [];

    if (current === 'pending' || current === 'assigned') {
      options.push({ text: '👍 Accept Order',     value: 'accepted'         });
      options.push({ text: '❌ Cancel Order',     value: 'cancelled', style: 'destructive' });
    } else if (['accepted', 'confirmed', 'processing'].includes(current)) {
      options.push({ text: '🚚 Out for Delivery', value: 'out-for-delivery' });
      options.push({ text: '❌ Cancel Order',     value: 'cancelled', style: 'destructive' });
    } else if (current === 'out-for-delivery') {
      options.push({ text: '✅ Mark Delivered',   value: 'delivered'        });
      options.push({ text: '❌ Cancel Order',     value: 'cancelled', style: 'destructive' });
    } else {
      alert('No status update available for this order');
      return;
    }

    const message = options.map((opt, i) => `${i + 1}. ${opt.text}`).join('\n');
    const choice  = window.prompt(`Update Order:\n\n${message}\n\nEnter your choice (1-${options.length}):`);

    if (choice) {
      const index = parseInt(choice) - 1;
      if (index >= 0 && index < options.length) {
        const selected = options[index];
        if (selected.style === 'destructive') {
          if (window.confirm('Are you sure you want to cancel this order?')) {
            handleStatusUpdate(order, selected.value);
          }
        } else {
          handleStatusUpdate(order, selected.value);
        }
      }
    }
  };

  const openMaps = (address) => {
    if (!address || address === '—') return alert('No address available');
    window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
  };

  const callCustomer = (phone) => {
    if (!phone || phone === '—') return alert('No phone number available');
    window.location.href = `tel:${String(phone).replace(/[^0-9+]/g, '')}`;
  };

  if (loading) return <LoadingSpinner message="Loading orders..." />;

  return (
    <div className="rider-orders">
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <span className="title-icon">📦</span>
              My Orders
            </h1>
            <p className="page-subtitle">Manage your deliveries</p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        {/* Stats */}
        <div className="rider-stats-bar">
          <div className="stat-item">
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">Active</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.delivered}</span>
            <span className="stat-label">Delivered</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">Rs. {stats.monthEarnings}</span>
            <span className="stat-label">Month Earnings</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          {[
            { key: 'all',       label: `All (${stats.total})`          },
            { key: 'active',    label: `Active (${stats.active})`      },
            { key: 'delivered', label: `Delivered (${stats.delivered})` },
            { key: 'cancelled', label: `Cancelled (${stats.cancelled})` },
          ].map(tab => (
            <button
              key={tab.key}
              className={`filter-tab ${filter === tab.key ? 'active' : ''}`}
              onClick={() => setFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h2 className="empty-state-title">
              {filter === 'all'       ? 'No orders available'  :
               filter === 'active'   ? 'No active orders'     :
               filter === 'delivered'? 'No delivered orders'  : 'No cancelled orders'}
            </h2>
            <p className="empty-state-text">Pull to refresh or wait for new orders.</p>
          </div>
        ) : (
          <div className="rider-orders-list">
            {filteredOrders.map((order) => {
              const status     = order?.status?.toLowerCase() || 'pending';
              const orderNo    = order?.order_number || `#${order?.id}`;
              const address    = order?.delivery_address || order?.deliveryAddress || '—';
              const phone      = order?.delivery_phone   || order?.deliveryPhone   || '—';
              const isUpdating = updatingOrderId === order?.id;
              const canUpdate  = riderRole === 'rider' && status !== 'delivered' && status !== 'cancelled';

              return (
                <div key={order.id} className="rider-order-card">
                  <div className="order-card-header">
                    <span className="order-number">{orderNo}</span>
                    <span className={`order-status status-${status}`}>
                      {status.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="order-card-body">
                    <div className="order-info-row">
                      <span className="info-icon">📍</span>
                      <span className="info-text">{address}</span>
                    </div>
                    <div className="order-info-row">
                      <span className="info-icon">📞</span>
                      <span className="info-text">{phone}</span>
                    </div>
                    <div className="order-card-footer">
                      <span className="order-amount">Rs. {order?.total_amount || 0}</span>
                      <span className="order-date">
                        {order?.created_at
                          ? new Date(order.created_at).toLocaleString('en-US', {
                              month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className="order-card-actions">
                    <button className="action-btn btn-navigate" onClick={() => openMaps(address)} disabled={isUpdating}>
                      🗺️ Navigate
                    </button>
                    <button className="action-btn btn-call" onClick={() => callCustomer(phone)} disabled={isUpdating}>
                      📞 Call
                    </button>
                    <button
                      className={`action-btn btn-update ${!canUpdate ? 'disabled' : ''}`}
                      onClick={() => canUpdate && showStatusOptions(order)}
                      disabled={isUpdating || !canUpdate}
                    >
                      {isUpdating ? <span className="spinner-small"></span> : '📝 Update'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}